/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import config from '../../../../share/config';
import session from '../../../../share/session';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',//実行中
  EXECUTE: 'execute',//仕向地変更確認
  CHANGE: 'change',//仕向地変更
  INPUT: 'input',//仕向地入力
  CONFIRMATION: 'confirmation',//初期化実行確認
  FAILED: 'failed'//共通エラー
};

export default {
  name: 'requirement085',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      destination: '',
      inputDestination: '',//確定入力値
      inputData: '',//入力値
      message: '',
      destinationCode: '',
      i18nWsDidKey: null,
      modal_requirement_085: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
          return 'danger';
        default:
          return 'secondary';
      }
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_085 = true;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_085 = false;
    },

     /**
	    * API 通信で共通バリデーションでエラーが発生した際のコールバック
      * @param {object} result バリデーション結果
      */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** データ読み出し処理 */
    async execute() {
      try {
        this.isLoading = true;
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        // データ読み出し(要求)（バックエンドAPI_POST） Getのポーリング処理も行う
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'start'
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // ポーリングが正常に完了した場合
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了：読み出しデータ判別
              if(response.data.user_data.initialize === true){
                // 初期化遷移
                this.phase = PHASE.CONFIRMATION;
              }else{
                // 読出しデータ表示
                this.destination = response.data.user_data.value;
                this.setExecuteMessage();
                this.phase = PHASE.EXECUTE;

              }
              break;
            case config.STATUS_WSP_FAILED:
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 仕向地変更確認のメッセージ設定 */
    setExecuteMessage(){
      let destinationText = "";
      // 仕向地設定の表示文言を取得
      switch (this.destination) {
        case "00":
          destinationText = this.$t(`${this.i18nWsDidKey}.1148_1_all_00`);
          break;
        case "01":
          destinationText = this.$t(`${this.i18nWsDidKey}.1148_1_all_01`);
          break;
        case "02":
          destinationText = this.$t(`${this.i18nWsDidKey}.1148_1_all_02`);
          break;
        case "03":
          destinationText = this.$t(`${this.i18nWsDidKey}.1148_1_all_03`);
          break;
        case "04":
          destinationText = this.$t(`${this.i18nWsDidKey}.1148_1_all_04`);
          break;
        case "05":
          destinationText = this.$t(`${this.i18nWsDidKey}.1148_1_all_05`);
          break;
        case "06":
          destinationText = this.$t(`${this.i18nWsDidKey}.1148_1_all_06`);
          break;
        case "07":
          destinationText = this.$t(`${this.i18nWsDidKey}.1148_1_all_07`);
          break;
        case "08":
        case "09":
        case "0A":
        case "0B":
        case "0C":
        case "0D":
          destinationText = this.$t(`${this.i18nWsDidKey}.1148_1_all_08_0D`);
          break;
        case "0E":
          destinationText = this.$t(`${this.i18nWsDidKey}.1148_1_all_0E`);
          break;
        case "0F":
          destinationText = this.$t(`${this.i18nWsDidKey}.1148_1_all_0F`);
          break;
        default:
          destinationText = this.$t(`${this.i18nWsDidKey}.1148_1_all_default`);
          break;
      }

      this.message = this.$t(`${this.i18nWsKey}.REQ_WS_85_TXT_1`)
        .replace('$DID$', destinationText);
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.FAILED;
    },
    
    /** 仕向地変更確認 YES*/
    async confirmProcess() {
      this.phase = PHASE.CHANGE;
      this.message = this.$t(`${this.i18nWsKey}.REQ_WS_85_TXT_2`)
                    .replace('$USER$', this.inputDestination);
      this.inputData = "";
    },
    
    /** 仕向地変更確認 NO*/
    async confirmInitProcess() {
      //データ書き込み
      this.phase = PHASE.CONFIRMATION;
    },
    
    /** 仕向地変更 入力*/
    async changeInputProcess() {
      this.inputData = this.inputDestination;
      this.phase = PHASE.INPUT;
    },
    
    /** 仕向地変更 OK*/
    async changeOkProcess() {
      //データ書き込み
      this.writeData();
    },

    /** 仕向地コード変換 */
    changeDestinationCode(){
      //仕向地の入力無チェック
      if(this.inputDestination === ''){
        //入力無
        this.destinationCode = this.destination;
      }else{
        //入力有
        //コード変換可否チェック
        const substr = this.inputDestination.substr(0, 1);
        if((null !== substr.match(/([a-z]|[A-Z])/gi)) && (this.inputDestination.length >= 2)){
          //変換可能
          const checkStr = this.inputDestination.substr(0, 2);
          
          switch (checkStr) {
            case "JP":
              this.destinationCode = '00';
              break;
            case "KA":
            case "KC":
              this.destinationCode = '04';
              break;
            case "KS":
              this.destinationCode = '06';
              break;
            case "C0":
            case "C5":
              this.destinationCode = '0F';
              break;
            case "U4":
            case "U5":
            case "U6":
              this.destinationCode = '0E';
              break;
            case "EK":
            case "ER":
            case "EN":
              this.destinationCode = '03';
              break;
            case "K4":
              this.destinationCode = '05';
              break;
            case "C6":
              this.destinationCode = '07';
              break;
            default:
              this.destinationCode = '02';
              break;
          }
        }else{
          //変換不可能（先頭の一文字がアルファベットでない あるいは 二文字未満のコードが入力された場合）
        this.destinationCode = this.destination;
        }
      }
    },

    /** データ書き込み */
    async writeData() {
      try {
        this.changeDestinationCode();
        this.isLoading = true;
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        // データ読み出し(要求)（バックエンドAPI_POST） Getのポーリング処理も行う
         const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'write',
            value: this.destinationCode
          },
            // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // ポーリングが正常に完了した場合
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              this.phase = PHASE.CONFIRMATION;
              break;
            case config.STATUS_WSP_FAILED:
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },
    
    /** 仕向地変更 キャンセル*/
    async changeCancelProcess() {
      this.phase = PHASE.CONFIRMATION;
    },
    
    /** 仕向地入力 クリア*/
    async inputClearProcess() {
      //テキストボックスクリア
      this.inputData = "";
    },
    
    /** 仕向地入力 OK*/
    async inputOkProcess() {
      //テキストボックスの値を反映する
      this.inputDestination = this.inputData;
      this.phase = PHASE.CHANGE;
      this.message = this.$t(`${this.i18nWsKey}.REQ_WS_85_TXT_2`)
                    .replace('$USER$', this.inputDestination);
    },
    
    /** 仕向地入力 キャンセル*/
    async inputCancelProcess() {
      this.inputData = "";
      this.phase = PHASE.CHANGE;
      this.message = this.$t(`${this.i18nWsKey}.REQ_WS_85_TXT_2`)
                    .replace('$USER$', this.inputDestination);
    },
    
    /** 初期化実行確認 OK*/
    async initOkProcess() {
      // 初期化データ書き込み
      try {
        // NonDefaultSession遷移
        this.isLoading = true;
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        // データ読み出し(要求)（バックエンドAPI_POST） Getのポーリング処理も行う
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'init',
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );
        
        if (response) {
          // ポーリングが正常に完了した場合
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              this.closeWizard();
              break;
            case config.STATUS_WSP_FAILED:
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },
    
    /** 初期化実行確認 キャンセル*/
    async initCancelProcess() {
      this.closeWizard();
    },
  },

  async mounted() {
    this.init();
    const systemId = individualDiagnose.getCurrentSystemId(this);
    this.i18nWsDidKey = `wsDid${systemId}`;
    // データ読み出し
    this.execute();
  },
};